import { useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AuthContext } from "../../context/AuthContext";
import {
  Loader2,
  MoreVertical,
  Pencil,
  Plus,
  Stethoscope,
  Trash2,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import DoctorFormModal from "./DoctorFormModal";
import { doctorInitials, doctorToForm } from "../../utils/doctorHelpers";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AVATAR_COLORS = [
  "bg-violet-100 text-violet-700",
  "bg-blue-100 text-blue-700",
  "bg-emerald-100 text-emerald-700",
  "bg-amber-100 text-amber-700",
  "bg-rose-100 text-rose-700",
  "bg-cyan-100 text-cyan-700",
];

function avatarColor(name) {
  let hash = 0;
  for (let i = 0; i < (name || "").length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export default function MediciCurantiSection({
  token,
  canManage,
  apiBase = "/api/doctors",
}) {
  const auth = useContext(AuthContext);
  const activeStrutturaId = auth?.activeStruttura?.id;
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [modalForm, setModalForm] = useState(null);

  const fetchDoctors = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}${apiBase}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDoctors(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => d.msg || d).join(", ")
          : "Errore nel caricamento dei medici curanti";
      toast.error(msg);
      setDoctors([]);
    } finally {
      setLoading(false);
    }
  }, [token, activeStrutturaId, apiBase]); // eslint-disable-line react-hooks/exhaustive-deps -- refetch al cambio struttura

  useEffect(() => {
    fetchDoctors();
  }, [fetchDoctors]);

  const handleOpenAdd = () => {
    setEditingDoctor(null);
    setModalForm(doctorToForm(null));
    setShowModal(true);
  };

  const handleOpenEdit = (doctor) => {
    setEditingDoctor(doctor);
    setModalForm(doctorToForm(doctor));
    setShowModal(true);
  };

  const handleDelete = async (doctor) => {
    if (!window.confirm(`Eliminare ${doctor.name}?`)) return;
    try {
      await axios.delete(`${API_URL}${apiBase}/${doctor.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Medico eliminato");
      fetchDoctors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  const handleSaved = () => {
    fetchDoctors();
  };

  return (
    <>
      <Card className="card-base border-violet-200">
        <CardHeader className="p-4 md:p-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                <Stethoscope className="w-4 h-4 md:w-5 md:h-5 text-violet-500" />
                Medici Curanti
              </CardTitle>
              <CardDescription className="text-xs md:text-sm">
                {doctors.length} {doctors.length === 1 ? "medico registrato" : "medici registrati"}
              </CardDescription>
            </div>
            {canManage && (
              <Button
                size="sm"
                onClick={handleOpenAdd}
                className="bg-violet-600 hover:bg-violet-700"
              >
                <Plus className="w-4 h-4 mr-1" />
                Aggiungi medico
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0 md:pt-0">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
            </div>
          ) : doctors.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">
              Nessun medico registrato. Aggiungi il primo medico curante per selezionarlo nei fascicoli.
            </p>
          ) : (
            <div className="overflow-x-auto -mx-1">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-slate-500">
                    <th className="pb-2 pl-1 font-medium">Medico</th>
                    <th className="pb-2 font-medium hidden md:table-cell">Specializzazione</th>
                    <th className="pb-2 font-medium hidden md:table-cell">Telefono</th>
                    <th className="pb-2 font-medium hidden lg:table-cell">Email</th>
                    <th className="pb-2 font-medium hidden lg:table-cell">Email blisteraggio</th>
                    <th className="pb-2 font-medium text-center">Ospiti</th>
                    {canManage && <th className="pb-2 pr-1 font-medium text-right">Azioni</th>}
                  </tr>
                </thead>
                <tbody>
                  {doctors.map((doctor) => (
                    <tr key={doctor.id} className="border-b border-slate-100 last:border-0">
                      <td className="py-3 pl-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${avatarColor(doctor.name)}`}
                          >
                            {doctorInitials(doctor.name)}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="font-medium text-slate-900 truncate">{doctor.name}</span>
                              {doctor.is_default && (
                                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px] px-1.5 py-0">
                                  Predefinito
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 md:hidden truncate">
                              {doctor.specialization || "—"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 text-slate-600 hidden md:table-cell">
                        {doctor.specialization || "—"}
                      </td>
                      <td className="py-3 text-slate-600 hidden md:table-cell whitespace-nowrap">
                        {doctor.phone || "—"}
                      </td>
                      <td className="py-3 text-slate-600 hidden lg:table-cell max-w-[180px] truncate">
                        {doctor.email || "—"}
                      </td>
                      <td className="py-3 text-slate-600 hidden lg:table-cell max-w-[180px] truncate">
                        {doctor.blistering_email || "—"}
                      </td>
                      <td className="py-3 text-center text-slate-700 font-medium">
                        {doctor.ospiti_associati ?? 0}
                      </td>
                      {canManage && (
                        <td className="py-3 pr-1 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleOpenEdit(doctor)}
                            >
                              <Pencil className="w-3.5 h-3.5 text-slate-500" />
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreVertical className="w-3.5 h-3.5 text-slate-500" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  className="text-red-600 focus:text-red-600"
                                  onClick={() => handleDelete(doctor)}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Elimina
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-xs text-slate-500 mt-4 pt-3 border-t border-slate-200">
            I medici curanti possono essere selezionati nei fascicoli assistenziali, nel deblistering e nelle comunicazioni sanitarie.
          </p>
        </CardContent>
      </Card>

      {canManage && (
        <DoctorFormModal
          open={showModal}
          onOpenChange={setShowModal}
          token={token}
          initialForm={modalForm}
          editingDoctor={editingDoctor}
          onSaved={handleSaved}
          apiBase={apiBase}
        />
      )}
    </>
  );
}
